import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Bot,
  ProfitApiResponse,
  Subscription,
  SubscriptionBalance,
  Tabs,
} from './types';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactSession } from 'react-client-session';
import { Layout } from '../../components/Layout';
import Content from './Content';
import PeriodProfitChart from './Charts/PeriodProfitChart';
import { ProfitByPeriodApiResponse } from './Charts/types';

import ServicePaths from '../../bridge/utils/ServicePaths';

const Detail: React.FC = () => {
  const clusterBots = ReactSession.get('clusterBots');

  const { subscriptionId } = useParams();
  const navigate = useNavigate();

  const [bot, setBot] = useState<Bot | undefined>(undefined);
  const [subscription, setSubscription] = useState<Subscription | undefined>(
    undefined,
  );
  const [subscriptionBalance, setSubscriptionBalance] = useState<
    SubscriptionBalance | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(Tabs.Charts);

  const [profitData, setProfitData] = useState<ProfitApiResponse | undefined>(
    undefined,
  );

  const [profitByPeriodData, setProfitByPeriodData] = useState<
    ProfitByPeriodApiResponse | undefined
  >(undefined);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await axios({
        url: ServicePaths.DATA.GetClientSubscription,
        method: 'POST',
        data: {
          subscriptionId,
        },
      });

      if (!data?.bot) {
        return;
      }

      setBot(data.bot);
      setSubscription(data.subscription);
      setSubscriptionBalance(data.balance);
      setIsLoading(false);
    };

    fetch();
  }, [subscriptionId]);

  useEffect(() => {
    const fetch = async () => {
      if (!subscription) {
        return;
      }

      const { id } = subscription;

      const { data } = await axios({
        url: ServicePaths.DATA.GetClientBotProfits,
        method: 'POST',
        data: {
          subscriptionId: id,
        },
      });

      setProfitData(data);
    };

    fetch();
  }, [subscription]);

  useEffect(() => {
    const fetch = async () => {
      if (!subscription) {
        return;
      }

      const { id } = subscription;

      const { data } = await axios({
        url: ServicePaths.DATA.GetClientBotProfitsByPeriod,
        method: 'POST',
        data: {
          subscriptionId: id,
        },
      });

      setProfitByPeriodData(data);
    };

    fetch();
  }, [subscription]);

  const sectionsN = [
    Tabs.Charts,
    Tabs.Transactions,
    Tabs.Control,
    Tabs.ExtraInfo,
  ];

  const profits = [
    { label: 'Total Stable Profit', delta: profitData?.total.stable },
    { label: 'Total Asset Profit', delta: profitData?.total.asset },
    {
      label: !!profitData?.current.stable
        ? 'Current Stable Profit'
        : 'Current Asset Profit',
      delta: !!profitData?.current.stable
        ? profitData?.current.stable
        : profitData?.current.asset,
    },
    {
      label: 'No Algorithmic Difference',
      delta: profitData?.noAlgorithmicProgressionProfit,
    },
  ];

  const hasPositiveProfit = useMemo(() => {
    if (!profitData) {
      return undefined;
    }

    const hasCurrentStableProfit = !!profitData.current.stable;

    const currentProfit = hasCurrentStableProfit
      ? profitData.current.stable
      : profitData.current.asset;

    if (currentProfit === null) {
      return (profitData.noAlgorithmicProgressionProfit ?? 0) > 0;
    }

    return currentProfit > 0;
  }, [profitData]);

  return (
    <Layout hasPositiveProfit={hasPositiveProfit}>
      <Layout.SideMenu hasPositiveProfit={hasPositiveProfit}>
        {profitData ? (
          <Layout.SideMenuSection>
            <Layout.SideMenuTitle title="Profit" count={profits.length} />
            <>
              {profits.map((profit) => (
                <Layout.SideMenuItem
                  key={profit.label}
                  id={profit.label}
                  label={profit.label}
                  delta={profit.delta}
                />
              ))}
            </>
            <PeriodProfitChart data={profitByPeriodData} />
          </Layout.SideMenuSection>
        ) : undefined}

        <Layout.SideMenuSection>
          <Layout.SideMenuTitle title={'Section'} count={sectionsN.length} />
          <>
            {sectionsN.map((section) => (
              <Layout.SideMenuItem
                key={section}
                id={section}
                label={section}
                onClick={setActiveTab}
                isActive={activeTab === section}
              />
            ))}
          </>
        </Layout.SideMenuSection>

        <Layout.SideMenuSection>
          <Layout.SideMenuTitle
            title="Subscriptions"
            count={clusterBots.length}
          />
          <>
            {clusterBots.map((sub: any) => (
              <Layout.SideMenuItem
                key={sub.id}
                id={sub.id}
                label={sub.botFriendlyName}
                value={sub.totalBalanceInStable}
                delta={sub.currentProfit}
                onClick={(subscriptionId: number) => {
                  navigate(`/sub/${subscriptionId}`, { replace: true });
                }}
              />
            ))}
          </>
        </Layout.SideMenuSection>
      </Layout.SideMenu>

      <Layout.Content>
        <Content
          isLoading={isLoading}
          bot={bot}
          subscription={subscription}
          subscriptionBalance={subscriptionBalance}
          activeTab={activeTab}
        />
      </Layout.Content>
    </Layout>
  );
};

export default Detail;
